import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { familyApi, type FamilyMember } from '../api/familyApi';
import { FamilyContext, type FamilyContextValue } from './familyContextStore';

const activeProfileStorageKey = (accountUserId: string) =>
  `ma_family_active_profile_v1_${accountUserId}`;
const activeProfileRoleStorageKey = (accountUserId: string) =>
  `ma_family_active_profile_role_v1_${accountUserId}`;

function readPersistedActiveProfileId(accountUserId: string): string | null {
  if (typeof window === 'undefined' || !accountUserId.trim()) return null;
  try {
    const v = localStorage.getItem(
      activeProfileStorageKey(accountUserId.trim()),
    );
    return v?.trim() || null;
  } catch {
    return null;
  }
}

function writePersistedActiveProfileId(
  accountUserId: string,
  memberId: string | null,
) {
  if (typeof window === 'undefined' || !accountUserId.trim()) return;
  try {
    const key = activeProfileStorageKey(accountUserId.trim());
    if (memberId?.trim()) localStorage.setItem(key, memberId.trim());
    else localStorage.removeItem(key);
  } catch {
    /* ignore quota / private mode */
  }
}

function writePersistedActiveProfileRole(
  accountUserId: string,
  role: string | null,
) {
  if (typeof window === 'undefined' || !accountUserId.trim()) return;
  try {
    const key = activeProfileRoleStorageKey(accountUserId.trim());
    if (role?.trim()) localStorage.setItem(key, role.trim());
    else localStorage.removeItem(key);
  } catch {
    /* ignore quota / private mode */
  }
}

export const FamilyProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAuthenticated, isDoctor } = useAuth();
  const [activeUserId, setActiveUserIdState] = useState<string | null>(null);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const initialFetchDone = useRef(false);
  /** True for the full `setActiveUserId` async window; blocks the persist `useEffect` from overwriting localStorage mid-switch. */
  const profileScopeSwitchInProgressRef = useRef(false);

  const normalizeMember = useCallback(
    (member: FamilyMember): FamilyMember => ({
      ...member,
      id: String(member.id),
    }),
    [],
  );

  /**
   * Backfill empty avatar from the logged-in user only when the "self" row is
   * **that** user. When acting as a dependent/shared profile, `isSelf` marks
   * the acting profile — it must not inherit the auth user's photo.
   */
  const withSelfAvatar = useCallback(
    (members: FamilyMember[]): FamilyMember[] =>
      members.map((rawMember) => {
        const member = normalizeMember(rawMember);
        if (!member.isSelf) return member;
        const isAuthUserRow =
          user?.id != null && String(member.id) === String(user.id);
        return {
          ...member,
          avatar:
            member.avatar ||
            (isAuthUserRow ? (user?.avatar as string) || '' : '') ||
            '',
        };
      }),
    [normalizeMember, user?.id, user?.avatar],
  );

  const fetchMembers = useCallback(async () => {
    if (!isAuthenticated || isDoctor()) return;
    if (!initialFetchDone.current) setLoading(true);
    const buildSelfMember = (): FamilyMember =>
      ({
        id: String(user?.id),
        name: user?.name || user?.email || 'You',
        email: user?.email || '',
        avatar: (user?.avatar as string) || '',
        dob: (user?.dob as string) || '',
        gender: (user?.gender as string) || '',
        relationship: 'self',
        isSelf: true,
      }) as FamilyMember;

    try {
      const accountIdForScope = user?.id != null ? String(user.id) : '';
      const intendedActing = accountIdForScope
        ? readPersistedActiveProfileId(accountIdForScope)
        : null;
      const res = await familyApi.getMembers(intendedActing);
      if (res.success && res.data && res.data.length > 0) {
        const normalizedMembers = withSelfAvatar(res.data);
        setFamilyMembers(normalizedMembers);
        const members = normalizedMembers;
        const accountId = user?.id != null ? String(user.id) : '';
        setActiveUserIdState((prev) => {
          // Priority order:
          //   1. `stored` (localStorage) — this is the explicit user intent
          //      and is always written **before** a /family/members fetch
          //      by `setActiveUserId`. It must win over `prev`, otherwise a
          //      switch where the previously-active profile also appears in
          //      the newly-scoped tree (e.g. A → B when B has reciprocally
          //      shared back to A) would "stick" on A.
          //   2. `prev` — keep whatever was already active if it's still
          //      in the tree. Covers background refreshes (visibilitychange,
          //      etc.) where no switch is in flight.
          //   3. `self` — fall back to the scoped tree's own self row.
          const ids = new Set(members.map((m) => m.id));
          const self = members.find((m) => m.isSelf);
          const stored = accountId
            ? readPersistedActiveProfileId(accountId)
            : null;
          if (stored && ids.has(stored)) return stored;
          if (prev && ids.has(prev)) return prev;
          return self?.id ?? null;
        });
      } else if (user?.id) {
        const selfMember = buildSelfMember();
        setFamilyMembers([selfMember]);
        setActiveUserIdState(selfMember.id);
        writePersistedActiveProfileId(String(user.id), selfMember.id);
      }
    } catch {
      if (user?.id) {
        const selfMember = buildSelfMember();
        setFamilyMembers([selfMember]);
        setActiveUserIdState(selfMember.id);
        writePersistedActiveProfileId(String(user.id), selfMember.id);
      }
    } finally {
      initialFetchDone.current = true;
      setLoading(false);
    }
  }, [
    isAuthenticated,
    isDoctor,
    user?.id,
    user?.name,
    user?.email,
    user?.dob,
    user?.gender,
    user?.avatar,
    withSelfAvatar,
  ]);

  useEffect(() => {
    if (!isAuthenticated || isDoctor()) {
      setFamilyMembers([]);
      setActiveUserIdState(null);
      initialFetchDone.current = false;
      return;
    }
    fetchMembers();
  }, [isAuthenticated, isDoctor, fetchMembers]);

  // Live refresh on family invite / share / ownership socket events
  useEffect(() => {
    if (!isAuthenticated || isDoctor()) return;
    const onFamilyChanged = () => {
      void fetchMembers();
    };
    window.addEventListener('meraayurved:family-changed', onFamilyChanged);
    return () => {
      window.removeEventListener('meraayurved:family-changed', onFamilyChanged);
    };
  }, [isAuthenticated, isDoctor, fetchMembers]);

  useEffect(() => {
    if (!isAuthenticated || isDoctor() || user?.id == null) return;
    if (isSwitching || profileScopeSwitchInProgressRef.current) return;
    const accountId = String(user.id);
    const activeMember =
      familyMembers.find((m) => m.id === activeUserId) ??
      familyMembers.find((m) => m.isSelf) ??
      null;
    // IMPORTANT: Do not clear persisted selection during the initial fetch.
    // On a hard refresh, `activeUserId` is null on first render, and `familyMembers`
    // may still be empty. Clearing here would wipe the stored profile selection
    // before `fetchMembers()` gets a chance to restore it.
    if (!initialFetchDone.current) return;

    // Only persist when we have a concrete active member.
    if (activeMember?.id) {
      writePersistedActiveProfileId(accountId, activeMember.id);
      writePersistedActiveProfileRole(accountId, activeMember.role ?? null);
    }
  }, [
    isAuthenticated,
    isDoctor,
    user?.id,
    familyMembers,
    activeUserId,
    isSwitching,
  ]);

  const setActiveUserId = useCallback(
    async (id: string | null) => {
      profileScopeSwitchInProgressRef.current = true;
      setIsSwitching(true);
      if (user?.id != null) {
        const accountId = String(user.id);
        writePersistedActiveProfileId(accountId, id);
        const role =
          id != null
            ? (familyMembers.find((m) => m.id === id)?.role ?? null)
            : null;
        writePersistedActiveProfileRole(accountId, role);
      }
      try {
        await fetchMembers();
      } finally {
        profileScopeSwitchInProgressRef.current = false;
        setIsSwitching(false);
      }
    },
    [user?.id, familyMembers, fetchMembers],
  );

  const selfMember = familyMembers.find((m) => m.isSelf) ?? null;

  const value: FamilyContextValue = {
    activeUserId,
    setActiveUserId,
    familyMembers,
    loading,
    isSwitching,
    refreshFamilyMembers: fetchMembers,
    selfMember,
  };

  return (
    <FamilyContext.Provider value={value}>{children}</FamilyContext.Provider>
  );
};
